import { useAuth } from '@schooly/components/authentication';
import { PageHeader, PageHeaderSearchInput } from '@schooly/components/filters';
import { AccessDenied, Loading, PlusIcon } from '@schooly/style';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { NoListItemsStub } from '../../components/common/NoListItemsStub/NoListItemsStub';
import { useUserRolesForSchool } from '../../context/userRoles/useUserRolesForSchool';
import useAppLocation from '../../hooks/useAppLocation';
import { useSchool } from '../../hooks/useSchool';
import UserRoleCategoryList from './UserRoleCategoryList';

const UserRoles: React.FC = () => {
  const { userRoles, isLoading, error } = useUserRolesForSchool();
  const { permissions } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const { hasSystemRole } = useSchool();
  const { formatMessage } = useIntl();
  const showActionButton = permissions.includes('userrole_manager');
  const showSearch =
    permissions.includes('userrole_manager') || permissions.includes('userrole_viewer');

  const location = useAppLocation();
  const navigate = useNavigate();

  const newUserRole = useCallback(() => {
    navigate('/userroles/new', {
      state: { backgroundLocation: location },
    });
  }, [location, navigate]);

  useEffect(() => {
    if (typeof hasSystemRole === 'boolean' && !hasSystemRole) {
      navigate('/settings');
    }
  }, [hasSystemRole, navigate]);

  if (isLoading || !userRoles) {
    return <Loading />;
  }

  if (error) {
    return <AccessDenied />;
  }

  const noRoles = !userRoles.available_to_assign.length && !userRoles.not_allowed_to_assign.length;

  return (
    <>
      <PageHeader
        pageTitleCounter={userRoles.available_to_assign.length ?? undefined}
        buttonTextId="userRoles-AddRole"
        pageTitleTextId="section-UserRoles"
        showActionButton={showActionButton}
        buttonIcon={<PlusIcon />}
        onButtonClick={newUserRole}
      >
        {showSearch && (
          <PageHeaderSearchInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder={formatMessage({ id: 'groups-SearchGroups' })}
          />
        )}
      </PageHeader>

      {noRoles ? (
        <NoListItemsStub titleText={<FormattedMessage id="userRoles-NoRolesYet" />} />
      ) : (
        <UserRoleCategoryList userRoleList={userRoles} userType="staff" searchQuery={searchQuery} />
      )}
    </>
  );
};

export default UserRoles;
